import type { MemberInterface, MemberPermission, RoleEscalationEligibility, RoleInterface, RoleInterfaceMap } from "@swivl-mono/types";

/**
 * Role-based escalation-eligibility resolution. Replaces the per-member
 * `routingGroupMemberships` JSONB approach from Phase I. See
 * `docs/snowy/plans/2026-XX-XX-routing-groups-phase-ii.md` § "Member
 * eligibility".
 *
 * Two-layer model:
 *  1. Role default — `Role.escalationEligibility`. Null = inherit via the
 *     `parentSlug` chain.
 *  2. Per-permission override — optional `escalationEligibility` on each
 *     `MemberPermission` entry. When set, wins for that one assignment.
 *
 * Merge across a member's matching role assignments: most-permissive
 * wins ("all" > "tickets-only" > "none").
 *
 * Ultimate fallback when role + ancestors + override are all null is
 * `"all"` — preserves existing behavior for orgs that haven't configured
 * the new field yet.
 *
 * Pure functions. Callers load the `RoleInterfaceMap` once and pass it
 * in; no I/O performed here.
 */

const RANK: Record<RoleEscalationEligibility, number> = {
  none: 0,
  "tickets-only": 1,
  all: 2
};

/** Human-readable label for an eligibility value. Used by the Studio UI. */
export function humanizeEligibility(value: RoleEscalationEligibility): string {
  switch (value) {
    case "all":
      return "All — receive escalations and tickets";
    case "tickets-only":
      return "Tickets only — no live escalations";
    case "none":
      return "None — excluded from both";
  }
}

/**
 * Walk the `parentSlug` chain to find the first non-null eligibility.
 * Returns `"all"` when nothing is set anywhere in the chain.
 */
export function resolveRoleEscalationEligibility(role: RoleInterface | undefined, rolesBySlug: RoleInterfaceMap): RoleEscalationEligibility {
  let cursor: RoleInterface | undefined = role;
  const seen = new Set<string>();
  while (cursor) {
    if (cursor.escalationEligibility) return cursor.escalationEligibility;
    if (!cursor.parentSlug) break;
    if (seen.has(cursor.parentSlug)) break; // defensive: cycle guard
    seen.add(cursor.parentSlug);
    cursor = rolesBySlug[cursor.parentSlug];
  }
  return "all";
}

function locationsMatch(perm: MemberPermission, orgId: number, locationId: number | undefined): boolean {
  const m = perm.locations;
  if (!m) return false;
  if (m.orgId === orgId) return true;
  if (locationId != null && m.locationIds?.includes(locationId)) return true;
  // groupIds matching at "location" level is delegated to callers that have the
  // groupId in hand. For the simple "is this member effective at this location"
  // check, we accept org-wide or the explicit locationId. Callers that need to
  // also accept group-scope permissions can compute groupId separately and
  // call `effectiveEscalationEligibilityWithGroup` (helper below) instead.
  return false;
}

/**
 * For a member at a given location, compute the strongest
 * eligibility across all their matching role assignments.
 *
 * The location-scope check is intentionally narrow: org-wide OR an
 * explicit `locationIds` match. If you need to also accept
 * `groupIds`-scope permissions (i.e. the member's permission grants
 * them in this location's parent group), use
 * `effectiveEscalationEligibilityWithGroup` and pass the location's
 * `groupId`.
 */
export function effectiveEscalationEligibility(
  member: MemberInterface | undefined,
  orgId: number,
  locationId: number | undefined,
  rolesBySlug: RoleInterfaceMap
): RoleEscalationEligibility {
  if (!member?.permissions?.length) return "all";
  let best: RoleEscalationEligibility = "none";
  for (const perm of member.permissions) {
    if (!locationsMatch(perm, orgId, locationId)) continue;
    const resolved = perm.escalationEligibility ?? resolveRoleEscalationEligibility(rolesBySlug[perm.roleSlug], rolesBySlug);
    if (RANK[resolved] > RANK[best]) best = resolved;
    if (best === "all") return "all"; // can't get more permissive — short-circuit
  }
  // If the member has no MATCHING permissions at this location at all, treat as
  // ineligible rather than "all" — they don't have a role at this location.
  return best;
}

/**
 * Variant that also accepts permissions scoped to a parent `groupId`
 * (e.g. the location's parent group as resolved from
 * `LocationEntity.groupId`). Callers that hold the location's groupId
 * should prefer this — it correctly accepts a member whose
 * `MemberPermission.locations.groupIds` includes the location's
 * containing group.
 */
export function effectiveEscalationEligibilityWithGroup(
  member: MemberInterface | undefined,
  orgId: number,
  groupId: number | undefined,
  locationId: number | undefined,
  rolesBySlug: RoleInterfaceMap
): RoleEscalationEligibility {
  if (!member?.permissions?.length) return "all";
  let best: RoleEscalationEligibility = "none";
  for (const perm of member.permissions) {
    const m = perm.locations;
    const orgMatch = m?.orgId === orgId;
    const groupMatch = groupId != null && !!m?.groupIds?.includes(groupId);
    const locMatch = locationId != null && !!m?.locationIds?.includes(locationId);
    if (!orgMatch && !groupMatch && !locMatch) continue;
    const resolved = perm.escalationEligibility ?? resolveRoleEscalationEligibility(rolesBySlug[perm.roleSlug], rolesBySlug);
    if (RANK[resolved] > RANK[best]) best = resolved;
    if (best === "all") return "all";
  }
  return best;
}

/**
 * Scope-AGNOSTIC variant: the strongest eligibility across ALL of the
 * member's role assignments, ignoring where each one applies.
 *
 * Use for coarse "can this member handle escalations ANYWHERE?" gates —
 * e.g. deciding whether to build them a Group Inbox nav rail at all. It
 * answers a question the scoped helpers structurally cannot: they take a
 * `locationId`/`groupId` and correctly return `"none"` when given
 * `undefined`, because to them `undefined` means "this work item has no
 * location", not "I'm not asking about a location" (ENG-2940).
 *
 * NOT an authorization check. Never use this to decide whether a member
 * may act on a specific ticket or location — that must go through
 * `effectiveEscalationEligibility` / `effectiveEscalationEligibilityWithGroup`,
 * which verify the member's permission actually covers that scope.
 */
export function effectiveEscalationEligibilityAnyScope(
  member: MemberInterface | undefined,
  rolesBySlug: RoleInterfaceMap
): RoleEscalationEligibility {
  if (!member?.permissions?.length) return "all";
  let best: RoleEscalationEligibility = "none";
  for (const perm of member.permissions) {
    const resolved = perm.escalationEligibility ?? resolveRoleEscalationEligibility(rolesBySlug[perm.roleSlug], rolesBySlug);
    if (RANK[resolved] > RANK[best]) best = resolved;
    if (best === "all") return "all"; // can't get more permissive — short-circuit
  }
  return best;
}
